import { useEffect, useState } from "react";
import { api } from "./workspaceApi";

/** Модель витрины в кабинете администратора — в том числе скрытая и модель Egel. */
export type AdminCatalogModel = {
  id: string; name: string; description: string; category: string; origin: "builtin" | "project";
  width: number; height: number; depth: number; coverUrl: string;
  published: boolean; customized: boolean;
  project: { id: string; name: string; deleted: boolean } | null;
};
type Filter = "all" | "wardrobe" | "shelving" | "chest" | "hidden";
const FILTERS: [Filter, string][] = [["all", "Все"], ["wardrobe", "Шкафы"], ["shelving", "Стеллажи"], ["chest", "Комоды"], ["hidden", "Скрытые"]];
const CATEGORY: Record<string, string> = { wardrobe: "Шкаф", shelving: "Стеллаж", chest: "Комод" };

/**
 * Витрина «Готовые изделия Egel» в кабинете (08.10): все модели, и Egel, и
 * опубликованные дизайнерами. Править — модель открывается в конструкторе как
 * проект кабинета, дальше её обновляет обычная публикация из этого проекта.
 */
export function CatalogAdmin({ busy, run, notify, onEdit, onCount }: {
  busy: boolean;
  run: (action: () => Promise<void>) => Promise<void>;
  notify: (message: string) => void;
  onEdit: (model: AdminCatalogModel) => void;
  onCount?: (count: number) => void;
}) {
  const [models, setModels] = useState<AdminCatalogModel[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const refresh = async () => {
    const list = (await api<{ models: AdminCatalogModel[] }>("/admin/catalog")).models;
    setModels(list); onCount?.(list.length);
  };
  useEffect(() => { void run(refresh); }, []);
  const replace = (model: AdminCatalogModel) => setModels(list => list?.map(entry => entry.id === model.id ? model : entry) ?? null);
  const path = (model: AdminCatalogModel) => `/admin/catalog/${encodeURIComponent(model.id)}`;
  const shown = (models ?? []).filter(model =>
    (filter === "all" || (filter === "hidden" ? !model.published : model.category === filter)) &&
    `${model.name} ${model.description} ${model.project?.name ?? ""}`.toLowerCase().includes(search.toLowerCase()));
  return <section className="catalog-admin">
    <p className="workspace-muted">Чтобы изменить модель, нажмите «Править в конструкторе»: она откроется как проект кабинета.
      После правок откройте кабинет → «Сейчас в конструкторе» → «Публикация в витрине» → «Обновить модель в витрине».
      Там же меняются название, описание и обложка. Правка модели Egel сохраняется и при обновлениях сайта; исходный вариант можно вернуть.</p>
    <div className="workspace-library-bar">
      <input aria-label="Поиск моделей" placeholder="Название или проект…" value={search} onChange={e => setSearch(e.target.value)} />
      <button type="button" disabled={busy} onClick={() => void run(refresh)}>Обновить</button>
    </div>
    <div className="catalog-admin__filters" role="group" aria-label="Раздел витрины">
      {FILTERS.map(([value, title]) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>
        {title}{value === "hidden" && models ? ` · ${models.filter(model => !model.published).length}` : ""}</button>)}
    </div>
    {models === null ? <p role="status">Загружаю модели витрины…</p> : !shown.length
      ? <div className="workspace-empty"><h3>Моделей не найдено</h3><p>Измените фильтр или поиск.</p></div>
      : <div className="workspace-grid catalog-admin__grid">{shown.map(model => <article key={model.id} className={model.published ? "" : "is-hidden"}>
        <img className="catalog-admin__cover" src={model.coverUrl} alt="" loading="lazy" />
        <div className="catalog-admin__badges">
          <span>{CATEGORY[model.category] ?? model.category}</span>
          <span>{model.origin === "builtin" ? "Модель Egel" : "Из проекта"}</span>
          {model.customized && model.origin === "builtin" && <span className="is-edited">Изменена</span>}
          {!model.published && <span className="is-hidden">Скрыта</span>}
        </div>
        <h3>{model.name}</h3>
        <p>{model.width} × {model.height} × {model.depth} мм</p>
        <small>{model.project ? `Правится в проекте «${model.project.name}»${model.project.deleted ? " (в корзине)" : ""}` : "Ещё не открывалась для правки"}</small>
        <button type="button" className="workspace-primary" disabled={busy} onClick={() => onEdit(model)}>Править в конструкторе →</button>
        <div className="catalog-admin__actions">
          <button type="button" disabled={busy} onClick={() => void run(async () => {
            if (model.published && !window.confirm(`Скрыть «${model.name}» с витрины? Клиенты перестанут её видеть; в кабинете она останется.`)) return;
            replace((await api<{ model: AdminCatalogModel }>(`${path(model)}/visibility`, "POST", { published: !model.published })).model);
            notify(model.published ? `«${model.name}» скрыта с витрины` : `«${model.name}» снова на витрине`);
          })}>{model.published ? "Скрыть с витрины" : "Показать на витрине"}</button>
          {model.origin === "builtin" && model.customized && <button type="button" disabled={busy} onClick={() => void run(async () => {
            if (!window.confirm(`Вернуть исходную модель Egel «${model.name}»? Правки витрины пропадут; проект, в котором её правили, останется в кабинете.`)) return;
            replace((await api<{ model: AdminCatalogModel }>(`${path(model)}/reset`, "POST", {})).model);
            notify("Исходная модель Egel возвращена");
          })}>Вернуть исходную</button>}
          {model.origin === "project" && <button type="button" className="is-danger" disabled={busy} onClick={() => void run(async () => {
            if (!window.confirm(`Удалить «${model.name}» с витрины совсем? Проект, из которого её опубликовали, останется.`)) return;
            await api(`${path(model)}/remove`, "POST", {});
            await refresh(); notify("Модель удалена с витрины");
          })}>Удалить с витрины</button>}
        </div>
      </article>)}</div>}
  </section>;
}
